import { useState } from 'react'

import { F0Dialog } from '@factorialco/f0-react'

import Loading from 'modules/core/components/Loading'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import useFormAccessGroup from 'modules/enps/hooks/useAccessGroup'
import type Form from 'modules/enps/store/models/Form'
import formAccessGroupMemberships from 'modules/surveys/store/collections/formAccessGroupMemberships'

interface Props {
  form: Form
  onClose: () => void
}

export default function AddEditorModal({ form, onClose }: Props) {
  const [selectedAccessId, setSelectedAccessId] = useState<string | undefined>(undefined)
  const [running, setRunning] = useState(false)
  const { loading, accessGroup } = useFormAccessGroup(form)

  const submit = async () => {
    if (!accessGroup || !selectedAccessId) {
      return
    }

    setRunning(true)

    const { success } = await asyncAction(() =>
      formAccessGroupMemberships.create({
        access_id: selectedAccessId,
        access_group_id: form.attributes.access_group_id,
        form_id: form.attributes.id,
      })
    )

    setRunning(false)

    if (success) onClose()
  }

  const memberIds = formAccessGroupMemberships.toArray()?.map((m) => m.attributes.id) || []

  return (
    <F0Dialog
      isOpen
      width="sm"
      title={i18n.t('enps.form.editors.title')}
      description={i18n.t('enps.form.editors.description')}
      onClose={onClose}
      primaryAction={{
        label: i18n.t('enps.form.editors.add_editor'),
        onClick: submit,
        disabled: !selectedAccessId || running,
        loading: running,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
      }}
    >
      {loading && <Loading />}
      {!loading && accessGroup && (
        <EmployeeSelectorV2
          value={selectedAccessId}
          onChange={setSelectedAccessId}
          useAccessId
          forcedFilters={{
            excludeCurrentEmployee: true,
            excludeIds: memberIds,
          }}
        />
      )}
    </F0Dialog>
  )
}
